import { type Location, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { isPredeliverRoute, isRolloverRoute } from '@/App/routes';
import { Spinner } from '@/share/Spinner/Spinner';
import { executeAction, formChangeAction } from '@/store/state/rfs/rfs.actions';
import { selectRfs, selectRfsStatus } from '@/store/state/rfs/rfs.selectors';
import { selectCurrentUser, selectNavigateAsUser, selectSelectedCompany } from '@/store/state/user/user.selectors';
import { selectBlockExecution } from '@/store/state/ui/ui.selectors';
import type { TradeActionType } from '@/models/trade';
import { useAppDispatch } from '@/store/store';
import { selectPredeliver } from '@/store/state/predeliver/predeliver.selectors';
import { sendHelpRequest, type IHelpRequestAdditionalParams } from '@/services/helpRequest.service';
import { getConfig } from '@/config/config';
import { http } from '@/utils/http';
import { getSgwtConnect } from '@/sgwtConnect';
import { DATE_FULL_FORMAT } from '@/utils/format';
import { isDefined } from '@sgme/fp';
import { toUtc } from '@/utils/dates';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';

const locales = { en: enUS };

interface ExecuteButtonProps {
  customClass?: string;
}

function getLabel({ pathname }: Location): TradeActionType | 'execute' {
  if (isRolloverRoute(pathname)) {
    return 'rollover';
  }
  if (isPredeliverRoute(pathname)) {
    return 'predeliver';
  }
  return 'execute';
}

export function ExecuteButton({ customClass = 'btn-xl col-12' }: ExecuteButtonProps) {
  const location = useLocation();
  const rfsStatus = useSelector(selectRfsStatus);

  const usrNavigateAsUser = useSelector(selectNavigateAsUser);
  const currentUser = useSelector(selectCurrentUser);
  const selectedCompany = useSelector(selectSelectedCompany);
  const blockExecution = useSelector(selectBlockExecution);

  const { trade } = useSelector(selectPredeliver);
  const rfs = useSelector(selectRfs);
  const usedAmount = rfs.masterAmount === 'sellAmount' ? rfs.sellAmount : rfs.buyAmount;

  const label = getLabel(location);
  const isExecutable = rfsStatus === 'quoting' && usrNavigateAsUser === undefined && currentUser!.canExecute && !blockExecution;
  const isExecuting = rfsStatus === 'executing';

  const formatAmount = new Intl.NumberFormat('en', {
    maximumFractionDigits: 5,
  }).format;

  const dispatch = useAppDispatch();

  const onExecute = () => {
    dispatch(executeAction());
  };

  const OnSendEmail = () => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const emailDate = format(now, 'dd MMMM yyyy', { locale: locales.en });
    const emailTime = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    const valueDate = isDefined(rfs.date) ? format(toUtc(new Date(rfs.date)), DATE_FULL_FORMAT, { locale: locales.en }) : null;

    const data = {
      clientEmail: currentUser?.email,
      xOneAFWref: trade?.reference,
      amount: `${formatAmount(rfs.masterAmount === 'buyAmount' ? rfs.buyAmount : rfs.sellAmount)} ${rfs.masterAmount === 'buyAmount' ? rfs.buyCurrency : rfs.sellCurrency}`,
      valueDate,
      clientName: selectedCompany?.companyName,
      clientMnemo: selectedCompany?.companyMnemonic,
      salesEmail: currentUser?.salesEmail,
      emailDate,
      emailTime,
    };

    const payload: IHelpRequestAdditionalParams = {
      application: 'SGME-MY-FX',
      config: 'afwpredeliver',
      details: JSON.stringify(data),
      data: data,

      templateId: getConfig().mailTemplateIds.templateIdForAFWpredeliver,
    };
    dispatch(formChangeAction({ isAmericanForwardEmailSent: true }));
    sendHelpRequest(http, getSgwtConnect())(payload).subscribe();
  };

  const isAmericanForwardDisabled = usedAmount === 0 || !isExecutable;

  return trade?.instrumentName !== 'FxoAmericanForward' ? (
    <button type="button" className={`btn btn-success execute ${customClass} ${isExecutable ? '' : 'disabled'}`} disabled={!isExecutable} onClick={onExecute}>
      <FormattedMessage id={`trader.executeButton.${label}`} /> {isExecuting && <Spinner />}
    </button>
  ) : (
    <button
      type="button"
      className={`btn btn-success execute ${customClass} ${!isAmericanForwardDisabled ? '' : 'disabled'}`}
      disabled={isAmericanForwardDisabled}
      onClick={OnSendEmail}
    >
      <FormattedMessage id="trader.executeButton.sendMail" />
    </button>
  );
}
